import { useAssetPriceSession } from '../lib/marketData/assetPriceSessionContext.js'
import { ibkrInstrumentQuoteStore } from '../lib/marketData/ibkrInstrumentQuoteStore.js'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { isOCCSymbol, parseOCC } from '../lib/occParser.js'
import {
  resolveQuoteDisplaySession,
  nextEquityQuoteSessionBoundary,
  selectPositionQuotePrice,
} from '../lib/marketData/ibkrQuoteSubscriptionStore.js'
import { ibkrQuoteSnapshotStore } from '../lib/marketData/ibkrQuoteSnapshotStore.js'
import { subscribeBrowserResume } from '../lib/marketData/browserResume.js'

let equitySessionClock = Date.now()
let equitySessionTimer = null
let stopEquitySessionResume = null
const equitySessionListeners = new Set()

function reconcileEquitySessionClock() {
  equitySessionClock = Date.now()
  for (const listener of equitySessionListeners) listener()
  if (equitySessionListeners.size > 0) armEquitySessionClock()
}

function armEquitySessionClock() {
  if (equitySessionTimer != null) clearTimeout(equitySessionTimer)
  const now = Date.now()
  const boundary = nextEquityQuoteSessionBoundary(now)
  if (boundary == null) return
  equitySessionTimer = setTimeout(() => {
    equitySessionTimer = null
    reconcileEquitySessionClock()
  }, Math.max(0, boundary - now))
}

function subscribeEquitySessionClock(listener) {
  equitySessionListeners.add(listener)
  if (equitySessionListeners.size === 1) {
    equitySessionClock = Date.now()
    armEquitySessionClock()
    stopEquitySessionResume = subscribeBrowserResume(reconcileEquitySessionClock)
  }
  return () => {
    equitySessionListeners.delete(listener)
    if (equitySessionListeners.size === 0) {
      if (equitySessionTimer != null) clearTimeout(equitySessionTimer)
      equitySessionTimer = null
      stopEquitySessionResume?.()
      stopEquitySessionResume = null
    }
  }
}

function getEquitySessionClockSnapshot() {
  return equitySessionClock
}

export function useEquityQuoteSessionClock() {
  return useSyncExternalStore(
    subscribeEquitySessionClock,
    getEquitySessionClockSnapshot,
    getEquitySessionClockSnapshot,
  )
}

function contractForSymbol(symbol) {
  if (!isOCCSymbol(symbol)) return { streamSymbol: symbol, params: {} }
  const occ = parseOCC(symbol)
  return occ ? {
    streamSymbol: occ.underlying,
    params: { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right },
  } : { streamSymbol: symbol, params: {} }
}

function useQuoteSnapshot(symbol, independent = false) {
  const contract = useMemo(() => contractForSymbol(symbol), [symbol])
  const shared = useAssetPriceSession()
  const instrumentId = !independent && shared?.instrumentsBySymbol?.[String(symbol || '').toUpperCase()]
  const awaitingIdentity = !independent && shared && !shared.ready
  const subscribe = useCallback(listener => {
    if (!contract.streamSymbol || awaitingIdentity) return () => {}
    if (instrumentId) return ibkrInstrumentQuoteStore.retain(instrumentId, listener)
    return ibkrQuoteSnapshotStore.retain(contract.streamSymbol, contract.params, listener)
  }, [contract, instrumentId, awaitingIdentity])
  const getSnapshot = useCallback(() => {
    if (!contract.streamSymbol || awaitingIdentity) return null
    if (instrumentId) return ibkrInstrumentQuoteStore.getSnapshot(instrumentId)
    return ibkrQuoteSnapshotStore.getSnapshot(contract.streamSymbol, contract.params)
  }, [contract, instrumentId, awaitingIdentity])
  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}

export function useIbkrQuoteSnapshot(symbol) { return useQuoteSnapshot(symbol) }
export function useIbkrMarketQuoteSnapshot(symbol) { return useQuoteSnapshot(symbol,true) }

export function useIbkrPositionQuoteSnapshot(symbol) {
  const snapshot = useIbkrQuoteSnapshot(symbol)
  const sessionClock = useEquityQuoteSessionClock()
  return useMemo(() => {
    if (!snapshot) return { snapshot: null, price: null, session: null }
    const secType = isOCCSymbol(symbol) ? 'OPT' : (snapshot.contract?.secType || 'STK')
    const session = resolveQuoteDisplaySession(snapshot, sessionClock)
    const displaySnapshot = session === snapshot.session
      ? snapshot
      : Object.freeze({ ...snapshot, session })
    return {
      snapshot: displaySnapshot,
      price: selectPositionQuotePrice(displaySnapshot, session, secType),
      session,
    }
  }, [snapshot, symbol, sessionClock])
}
